import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { AppResource } from 'src/app/app.resource';
import { PathType } from 'src/app/core/enums/path-type.enum';
import {
  NatureResidence,
  QualiteOccupant,
  TypeHabitation
} from 'src/app/core/models/form-state.model';
import { Question } from 'src/app/core/models/question.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import * as fromHousing from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';
import * as fromPath from '../../../core/state/path';

import { Stepper } from 'src/app/core/models/stepper.model';
import { State } from 'src/app/core/state/core.state';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import {
  NUMBER_PIECES_PATH,
  OWNER_TENANT_PATH,
  ROOMAREA_PATH,
  ROOT_PATH
} from 'src/app/shared/constants/route.constants';
import { PopUpInfoVM } from 'src/app/shared/models/components/popupinfoVm';

@Component({
  selector: 'app-house-apartment',
  templateUrl: './house-apartment.component.html',
  styleUrls: ['./house-apartment.component.scss']
})
export class HouseApartmentComponent extends BaseComponent implements OnInit {
  mouseOveredFlat = false;
  mouseOveredHouse = false;
  mouseOveredOther = false;

  flatSelected = false;
  houseSelected = false;
  otherSelected = false;
  roomSelected = false;

  nextStepEnabled = false;

  pathType = PathType;
  path: string = '';

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepHouseApartment,
    subStepNumber: 2
  };

  override question: Question = {
    libelle: this.resource.question.houseApartment,
    popupInfo: new PopUpInfoVM([this.resource.infobulle.house])
  };

  public override currentPage = MatomoConstants.TYPE_HABITATION;

  displayModal: boolean = false;

  contentWebCallBackPopup: string = '';

  constructor(store: Store<State>, resources: AppResource, private readonly router: Router) {
    super(store, resources);
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.updateResumeStepVisibility(true);
    this.loadStateData();
  }

  private async loadStateData() {
    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    let housingData = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );

    switch (housingData.SaisieHabitation.TypeHabitation) {
      case TypeHabitation.Maison:
        this.houseSelected = true;
        break;
      case TypeHabitation.Appartement:
        this.flatSelected = true;
        break;
      case TypeHabitation.Autre:
        this.otherSelected = true;
        break;
      case TypeHabitation.Chambre:
        this.roomSelected = true;
        break;
    }
  }

  onHouseSelected() {
    if (!this.houseSelected) this.houseSelected = !this.houseSelected;
    if (this.houseSelected) {
      this.otherSelected = false;
      this.flatSelected = false;
    }

    this.store.dispatch(
      fromHousing.patchHousingType({
        payload: { TypeHabitation: TypeHabitation.Maison }
      })
    );

    this.nextStep();
  }

  onFlatSelected() {
    if (!this.flatSelected) this.flatSelected = !this.flatSelected;
    if (this.flatSelected) {
      this.houseSelected = false;
      this.otherSelected = false;
    }
    this.store.dispatch(
      fromHousing.patchOccupantType({
        payload: { TypeHabitation: TypeHabitation.Appartement }
      })
    );

    this.nextStep();
  }

  async onOtherSelected() {
    if (this.path === PathType.HOUSING_ETUDIANTE) {
      this.displayModal = true;
    } else {
      this.otherSelected = !this.otherSelected;
      if (this.otherSelected) {
        this.houseSelected = false;
        this.flatSelected = false;
      }
    }

    this.store.dispatch(
      fromHousing.patchHousingType({
        payload: { TypeHabitation: TypeHabitation.Autre }
      })
    );
    this.displayModal = true;
  }

  onRoomSelected() {
    this.roomSelected = !this.roomSelected;
    if (this.roomSelected) {
      this.houseSelected = false;
      this.flatSelected = false;
      this.otherSelected = false;
    }

    this.store.dispatch(
      fromHousing.patchHousingType({
        payload: { TypeHabitation: TypeHabitation.Chambre }
      })
    );

    this.nextStep();
  }

  async nextStep() {
    await this.updateResumeStep();

    await this.updateStepper(this.path);

    if (this.path === PathType.HOUSING_SOLIDARITY) {
      this.store.dispatch(
        fromHousing.patchOccupantType({
          payload: { QualiteOccupant: QualiteOccupant.Locataire }
        })
      );

      this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
    } else if (this.path === PathType.HOUSING_ETUDIANTE && this.roomSelected) {
      // Define the number of person of housing
      this.store.dispatch(
        fromHousing.patchOccupantType({
          payload: { CompositionFoyer: 1 }
        })
      );
      this.router.navigate([`${ROOT_PATH}${ROOMAREA_PATH}`]);
    } else {
      if (this.path !== PathType.HOUSING_ETUDIANTE) {
        this.router.navigate([`${ROOT_PATH}${OWNER_TENANT_PATH}`]);
      } else {
        // Fix #11777 - For student always set occupant type to tenant
        this.store.dispatch(
          fromHousing.patchOccupantType({
            payload: { QualiteOccupant: QualiteOccupant.Locataire }
          })
        );
        this.store.dispatch(
          fromHousing.patchHousingNature({
            payload: { NatureResidence: NatureResidence.Principale }
          })
        );
        this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
      }
    }
  }

  cancel() {
    this.displayModal = false;
  }

  async updateStepper(path: string) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.houseapartment
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN;
    if (path == PathType.HOUSING_SOLIDARITY) {
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.numberpieces
      );
    } else {
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.ownertenant
      );
    }

    if (subtitleN) {
      subtitleN.isActive = true;
      subtitleN.isVisible = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep() {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );
    const tmpResumeStep = cloneResumeStep(resumeStep);

    const stepResumeStep = tmpResumeStep.steps?.find(
      x => x.id == this.resource.resumestep.houseapartment
    );

    if (stepResumeStep) {
      stepResumeStep.step = this.resource.header.subStepHouseApartment;
      stepResumeStep.isVisible = true;
    } else {
      tmpResumeStep.steps?.push({
        id: this.resource.resumestep.houseapartment,
        step: this.resource.header.subStepHouseApartment,
        url: this.resource.resumestep.houseapartment,
        isVisible: true
      });
    }
    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }

  setClassActive(value: boolean): string {
    return value ? 'active' : 'inactive';
  }
}
